import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Image as ImageIcon, Check, AlertCircle, Trash2, Upload, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal photo-picker for selecting a Roof Screenshot (Google-Earth grab
 * of the roof) for a property. Persists the chosen photo id on:
 *   • property.roof_screenshot_photo_id  (legacy alias: roof_diagram_photo_id)
 *
 * Iter179 — renamed from "Roof Diagram" to "Roof Screenshot" (Roof
 * Diagrams are now the upcoming vector templates; Roof Screenshots are
 * the actual photo underlays from Google Earth). Picker now also lets
 * the operator upload a fresh screenshot directly (drop-zone + file
 * input) and gives them a one-click deep-link to Google Earth pre-
 * filled with the property address.
 *
 * Props:
 *   propertyId   — required, drives the photo source list + upload target
 *   selectedId   — current value (string photo _id or '')
 *   onSelect(id) — called with new photo id (or '' to clear)
 *   onClose()    — close handler
 *   address      — optional human address used to seed the Google Earth deep-link
 */
export default function RoofDiagramPicker({ propertyId, selectedId, onSelect, onClose, address = '' }) {
  // iter303ai-fix11 · Defensive fallback — some inline call-sites (e.g. the
  // Workflow Runner's RoofScreenshotCapture renderer) may not need a close
  // behaviour; keep the picker resilient instead of throwing.
  const safeClose = onClose || (() => {});
  const { api } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(selectedId || '');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Iter180 — confirmation strip shown briefly after a successful
  // upload so the operator visually sees the photo land in the gallery
  // before clicking Save & continue.
  const [uploadConfirm, setUploadConfirm] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/photos/by-property/${propertyId}`);
      setPhotos(Array.isArray(data) ? data : []);
    } catch { setPhotos([]); }
    setLoading(false);
  }, [api, propertyId]);

  useEffect(() => { load(); }, [load]);

  const confirm = () => { onSelect(picked || ''); safeClose(); };
  const clear = () => { onSelect(''); safeClose(); };

  // Iter179 — upload handler. POSTs the chosen file as a property-
  // scoped screenshot (category=Screenshot) and auto-selects the new
  // photo so the operator can hit Use immediately without scrolling.
  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('property_id', propertyId);
      const { data } = await api.post('/photos/upload-property-screenshot', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Screenshot uploaded — review and Save & continue');
      await load();
      setPicked(data._id);
      setUploadConfirm({ name: file.name, ts: Date.now() });
      // Auto-clear the confirmation strip after a few seconds.
      setTimeout(() => setUploadConfirm(c => (c && Date.now() - c.ts >= 4000) ? null : c), 4200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const googleEarthHref = `https://earth.google.com/web/search/${encodeURIComponent(address.trim() || `property ${propertyId}`)}`;

  return (
    <div data-testid="roof-diagram-picker-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-[#E2E2DF]">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-[#D35400]" />
            <h2 className="text-base font-semibold text-slate-800">Select Roof Screenshot</h2>
            <span className="text-xs text-slate-400 ml-1">({photos.length} for this property)</span>
          </div>
          <button data-testid="roof-diagram-picker-close" onClick={safeClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 bg-slate-50 space-y-4">
          {/* Iter180 — green confirmation strip after a successful upload. */}
          {uploadConfirm && (
            <div data-testid="roof-screenshot-upload-confirm" className="flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-md px-3 py-2 text-sm text-emerald-800">
              <Check size={14} className="text-emerald-600" />
              <span><b>Uploaded:</b> {uploadConfirm.name}. Review the highlighted thumbnail below, then click <b>Save &amp; continue</b>.</span>
            </div>
          )}
          {/* Iter179 — top action bar: Google Earth deep link + Upload
              drop-zone. Lets the operator grab a fresh screenshot
              without leaving the picker. */}
          <div data-testid="roof-screenshot-upload-bar" className="bg-white rounded-lg border border-[#E2E2DF] p-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Step 1 · Capture from Google Earth</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Search opens with the property address; take a screenshot, then drop it below.</p>
              </div>
              <a
                data-testid="open-google-earth-btn"
                href={googleEarthHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold text-[#1A73E8] border border-[#1A73E8] px-3 py-1.5 rounded-md hover:bg-[#1A73E8]/5 whitespace-nowrap"
              >
                <ExternalLink size={12} /> Open Google Earth
              </a>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Step 2 · Upload screenshot</p>
            <div
              data-testid="roof-screenshot-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`relative cursor-pointer border-2 border-dashed rounded-lg px-4 py-6 flex flex-col items-center justify-center transition ${dragOver ? 'border-[#D35400] bg-[#FFF7ED]' : 'border-[#E2E2DF] hover:border-[#D35400] hover:bg-[#FFF7ED]/30'}`}
            >
              {uploading ? (
                <><Loader2 size={20} className="text-[#D35400] animate-spin mb-1" /><span className="text-xs text-slate-600">Uploading…</span></>
              ) : (
                <>
                  <Upload size={20} className="text-[#D35400] mb-1" />
                  <span className="text-xs font-semibold text-slate-700">Drop screenshot here, or click to pick a file</span>
                  <span className="text-[10px] text-slate-400 mt-0.5">PNG / JPG · saved as the property&apos;s Roof Screenshot</span>
                </>
              )}
              <input
                ref={fileInputRef}
                data-testid="roof-screenshot-file-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
              />
            </div>
          </div>

          {/* Existing photo library */}
          {loading ? (
            <p className="text-center text-sm text-slate-400 py-12">Loading photos…</p>
          ) : photos.length === 0 ? (
            <div data-testid="roof-diagram-picker-empty" className="text-center py-8 bg-white border border-[#E2E2DF] rounded-lg">
              <AlertCircle size={32} className="mx-auto text-amber-500 mb-2" />
              <p className="text-sm font-semibold text-slate-700">No screenshots uploaded for this property yet.</p>
              <p className="text-xs text-slate-500 mt-1">Use the drop-zone above to upload your Google Earth screenshot.</p>
            </div>
          ) : (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Or pick an existing photo</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map(p => {
                  const isPicked = picked === p._id;
                  const isScreenshot = p.category === 'Screenshot';
                  return (
                    <button
                      key={p._id}
                      data-testid={`roof-diagram-photo-${p._id}`}
                      onClick={() => setPicked(p._id)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all bg-white ${isPicked ? 'border-[#D35400] ring-2 ring-[#D35400]/40' : 'border-[#E2E2DF] hover:border-[#D35400]/60'}`}
                    >
                      <img src={p.url} alt={p.description || p.original_name || 'roof'} className="w-full h-32 object-cover" />
                      <div className="p-1.5 text-left">
                        <p className="text-[10px] text-slate-600 line-clamp-1">{p.description || p.original_name || '—'}</p>
                      </div>
                      {isScreenshot && (
                        <span className="absolute top-1 left-1 text-[8px] font-bold uppercase tracking-wider text-white bg-[#1A73E8] rounded-full px-1.5 py-0">Screenshot</span>
                      )}
                      {isPicked && (
                        <div className="absolute top-1 right-1 bg-[#D35400] text-white rounded-full p-1"><Check size={12} strokeWidth={3} /></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <footer className="flex items-center justify-between px-5 py-3 border-t border-[#E2E2DF] bg-white">
          <button
            data-testid="roof-diagram-picker-clear"
            onClick={clear}
            disabled={!selectedId}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          ><Trash2 size={12} /> Clear current screenshot</button>
          <div className="flex items-center gap-2">
            <button onClick={safeClose} className="text-sm px-3 py-1.5 rounded-md border border-[#E2E2DF] hover:bg-slate-50">Cancel</button>
            <button
              data-testid="roof-diagram-picker-confirm"
              onClick={confirm}
              disabled={!picked || picked === selectedId}
              className="text-sm px-3 py-1.5 rounded-md bg-[#D35400] text-white font-semibold hover:bg-[#B84500] disabled:opacity-40 disabled:cursor-not-allowed"
            >Save &amp; continue →</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
